import { Role } from '@/acl'
import { hasMenuActionPermission } from '@/acl/action-allowed-roles'
import { useRouteContext } from '@tanstack/react-router'

/**
 * Guard Component - Role-Based Access Control (RBAC) wrapper
 *
 * This component conditionally renders its children based on the user's roles.
 * It supports two modes:
 *
 * 1. **Action-based** (preferred): Uses `menu` + `action` to look up permissions
 *    from `action-allowed-roles.json` (generated from Excel Action-Role Matrix).
 *
 * 2. **Role-based** (legacy/fallback): Uses `roles` array for direct role checking.
 *
 * Resolution order:
 *   1. If `menu` + `action` provided → look up action-allowed-roles.json
 *   2. If found → check user roles against action's allowedRoles
 *   3. If NOT found → fall back to `roles` prop
 *   4. ADMIN and SUPER always pass (implicit)
 *
 * @example Action-based (preferred)
 *   <Guard menu="sale1010" action="createDocument">
 *     <Button>สร้างเอกสาร</Button>
 *   </Guard>
 *
 * @example Action-based with fallback roles
 *   <Guard menu="sale1010" action="createDocument" roles={[Role.SALES_OFFICER]}>
 *     <Button>สร้างเอกสาร</Button>
 *   </Guard>
 *
 * @example Role-based (legacy, still supported)
 *   <Guard roles={[Role.WAREHOUSE_OFFICER, Role.WAREHOUSE_SUPERVISOR]}>
 *     <ProtectedContent />
 *   </Guard>
 *
 * Behavior:
 *   - ADMIN and SUPER roles are ALWAYS allowed access
 *   - If `roles` is empty or undefined (and no action match): Only ADMIN and SUPER can see the content
 */
type GuardProps = {
  /** Menu code for action-based lookup (e.g., 'sale1010', 'inv5010') */
  menu?: string
  /** Action name for action-based lookup (e.g., 'createDocument', 'approve') */
  action?: string
  /**
   * Array of roles that are allowed to view the protected content.
   * Used as fallback when `menu` + `action` is not provided or not found in ACL.
   *
   * Note: ADMIN and SUPER roles are always implicitly included.
   */
  roles?: Role[]
  children: React.ReactNode
}

export const Guard: React.FC<GuardProps> = ({
  menu,
  action,
  roles,
  children,
}) => {
  const auth = useRouteContext({
    select: (state) => state.auth,
    from: '/_auth',
  })

  // If menu + action provided, try action-based lookup first
  if (menu && action) {
    const hasPermission = hasMenuActionPermission(menu, action, auth)
    if (hasPermission !== null) {
      return hasPermission ? <>{children}</> : null
    }
  }

  // Fallback: role-based check (legacy behavior)
  if (auth?.hasRequiredRole(roles)) {
    return <>{children}</>
  }

  return null
}
